import React, { useState, useEffect } from 'react';
import { reauthenticateWithCredential, EmailAuthProvider, updatePassword } from 'firebase/auth';
import { auth } from '../firebase';
import styles from './CambiarContraseña.module.css';

export default function CambiarContraseña() {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('');

  useEffect(() => {
    // Ocultamos menú al montar
    const configSection = document.querySelector('.configSection');
    if (configSection) configSection.style.display = 'none';

    // Volvemos a mostrar menú al desmontar
    return () => {
      if (configSection) configSection.style.display = '';
    };
  }, []);

  useEffect(() => {
    const strength = getPasswordStrength(newPassword);
    setPasswordStrength(strength);
  }, [newPassword]);

  const getPasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    switch (score) {
      case 0:
      case 1: return 'Débil';
      case 2: return 'Media';
      case 3:
      case 4: return 'Fuerte';
      default: return '';
    }
  };

  const handleSubmit = () => {
    setError('');
    setSuccess('');

    if (!oldPassword || !newPassword || !repeatPassword) {
      setError('Por favor, rellena todos los campos.');
      return;
    }

    if (newPassword.length < 8) {
      setError('La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }

    if (newPassword !== repeatPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    if (oldPassword === newPassword) {
      setError('La nueva contraseña no puede ser igual a la actual.');
      return;
    }

    setShowConfirm(true);
  };

  const confirmChange = async () => {
    const user = auth.currentUser;
    if (!user) return setError('Usuario no autenticado.');

    setLoading(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, oldPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setSuccess('Contraseña cambiada con éxito.');
      setOldPassword('');
      setNewPassword('');
      setRepeatPassword('');
      setPasswordStrength('');
    } catch (err) {
      console.error(err);
      setError('Error al cambiar la contraseña. Verifica la contraseña actual.');
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  return (
    <div className={`${styles.passwordContainer} fadeIn`}>
      <h2 className={styles.passwordTitle}>Cambiar contraseña</h2>
      <form className={styles.passwordForm} onSubmit={(e) => e.preventDefault()}>
        <input
          type="password"
          placeholder="Contraseña actual"
          className={styles.passwordInput}
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
        />
        <input
          type="password"
          placeholder="Nueva contraseña"
          className={styles.passwordInput}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        {newPassword && (
          <div style={{ fontWeight: 'bold', color: getStrengthColor(passwordStrength) }}>
            Fuerza: {passwordStrength}
          </div>
        )}
        <input
          type="password"
          placeholder="Repetir nueva contraseña"
          className={styles.passwordInput}
          value={repeatPassword}
          onChange={(e) => setRepeatPassword(e.target.value)}
        />

        {error && <div className={styles.errorMsg}>{error}</div>}
        {success && <div className={styles.successMsg}>{success}</div>}

        <button className={styles.submitBtn} onClick={handleSubmit} disabled={loading}>
          Cambiar contraseña
        </button>
      </form>

      {showConfirm && (
        <div className={`${styles.confirmModal} slideUp`}>
          <div className={styles.modalContent}>
            <p>¿Estás seguro de que deseas cambiar tu contraseña?</p>
            <div className={styles.modalButtons}>
              <button className={styles.cancelBtn} onClick={() => setShowConfirm(false)}>Cancelar</button>
              <button className={styles.confirmBtn} onClick={confirmChange} disabled={loading}>Confirmar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const getStrengthColor = (strength) => {
  switch (strength) {
    case 'Débil': return '#d32f2f';
    case 'Media': return '#f9a825';
    case 'Fuerte': return '#00c853';
    default: return '#666';
  }
};
